import { DEFAULT_CRYPTO_DECIMALS, ZERO_WEI } from '@kwenta/sdk/constants'
import { formatNumber, getDisplayAsset, stripZeros } from '@kwenta/sdk/utils'
import type Wei from '@kwenta/wei'
import { wei } from '@kwenta/wei'

import { type FC, memo, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import type { ConditionOrderTableItem } from 'types/futures'

import TextButton from 'components/Button/TextButton'
import InputTitle from 'components/Input/InputTitle'
import NumericInput from 'components/Input/NumericInput'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { FlexDivRow } from 'components/layout/flex'
import { selectEditCOModalInputs, selectPerpsProvider } from 'state/futures/common/selectors'
import { editSmartMarginConditionalOrderSize } from 'state/futures/isolatedMargin/actions'
import { editCrossMarginConditionalOrderSize } from 'state/futures/snxPerpsV3/actions'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { providerIsCrossMargin } from 'utils/futures'

type OrderSizingProps = {
	order: ConditionOrderTableItem
	maxNativeValue: Wei
	isMobile?: boolean
}

const EditOrderSizeInput: FC<OrderSizingProps> = memo(({ order, isMobile, maxNativeValue }) => {
	const dispatch = useAppDispatch()
	const { size } = useAppSelector(selectEditCOModalInputs)

	const perpsProvider = useAppSelector(selectPerpsProvider)
	const [isUserInput, setIsUserInput] = useState(false)

	const initialValue = useMemo(
		() => stripZeros(order.size.abs().toString(DEFAULT_CRYPTO_DECIMALS)),
		[order.size]
	)

	const onSizeChange = useCallback(
		(value: string) => {
			setIsUserInput(true)
			if (providerIsCrossMargin(perpsProvider)) {
				dispatch(editCrossMarginConditionalOrderSize(order, value))
			} else {
				dispatch(editSmartMarginConditionalOrderSize(order, value))
			}
		},
		[dispatch, order, perpsProvider]
	)

	const handleSetMax = useCallback(() => {
		onSizeChange(stripZeros(maxNativeValue.toString()))
	}, [onSizeChange, maxNativeValue])

	const onChangeValue = useCallback(
		(_: any, v: string) => {
			onSizeChange(v)
		},
		[onSizeChange]
	)

	const sizeWei = useMemo(() => {
		return !size || Number.isNaN(Number(size)) ? ZERO_WEI : wei(size)
	}, [size])

	const invalid = size !== '' && maxNativeValue.lt(sizeWei.abs())

	return (
		<OrderSizingContainer>
			<OrderSizingRow>
				<InputTitle>Size</InputTitle>
				<FlexDivRow onClick={handleSetMax}>
					<TextButton>Max</TextButton>
					<Body>
						{formatNumber(maxNativeValue, { suggestDecimals: true })} {getDisplayAsset(order.asset)}
					</Body>
				</FlexDivRow>
			</OrderSizingRow>

			<NumericInput
				invalid={invalid}
				dataTestId={`edit-position-size-input${isMobile ? '-mobile' : '-desktop'}`}
				value={isUserInput ? size : initialValue}
				placeholder="0.00"
				onChange={onChangeValue}
				right={<Body color="secondary">{getDisplayAsset(order.asset)}</Body>}
			/>
			<Spacer height={16} />
		</OrderSizingContainer>
	)
})

const OrderSizingContainer = styled.div`
	margin-top: 16px;
`

const OrderSizingRow = styled(FlexDivRow)`
	width: 100%;
	align-items: center;
	margin-bottom: 8px;
	cursor: default;
`

export default EditOrderSizeInput
